import * as logger from 'loglevel'
import JsonApiResource from './JsonApiResource'
import userMessaging from '../Utils/UserMessaging'

const HostUserType = "host"

const ParticipantsDisplayMessageType = 'PARTICIPANTS_DISPLAY'
const PrivateDisplayMessageType  = 'PRIVATE_DISPLAY'
const ForceShutdownMessageType = 'FORCE_SHUTDOWN'

export default class AnearParticipant extends JsonApiResource {
  constructor(json_data, json_included) {
    super(json_data, json_included)
    this.userId = this.relationships['user'].data.id
    this.eventId = this.relationships['event'].data.id
    this._urlContext = null
    this._channels =  {
      actionsChannel: null,
      privateChannel: null,
      participantsChannel: null
    }
    // Idempotency guards: ABR teardown can call leave multiple times (cancel + unmount + transition)
    this._leaveEventPromise = null
    this._detachingPromise = null
    this._hasLeftEvent = false
    this._hasLeftActionPresence = false
  }

  get userType() {
    return this.attributes["user-type"]
  }

  isHost() {
    return this.userType === HostUserType
  }

  getParticipantChannels = () => {
    if (this._channels.actionsChannel) return

    this._channels.actionsChannel = userMessaging.getChannel(this.attributes['actions-channel-name'])
    this._channels.privateChannel = userMessaging.getChannel(this.attributes['private-channel-name'])

    if (!this.isHost()) {
      this._channels.participantsChannel = userMessaging.getChannel(this.attributes['participants-channel-name'])
    }
  }

  initMessaging = () => {
    this.getParticipantChannels()
  }

  setUrlContext = (urlContext = null) => {
    this._urlContext = urlContext && typeof urlContext === 'object'
      ? { ...urlContext }
      : null
  }

  setActionPresence = async (requiresGeoLocation=false) => {
    const args = [
      this._channels.actionsChannel,
      this.id,
      requiresGeoLocation
    ]
    if (this._urlContext) args.push(this._urlContext)
    await userMessaging.setChannelPresence(...args)
  }

  leaveActionPresence = async (options = {}) => {
    logger.debug(`AnearParticipant.leaveActionPresence(options: ${JSON.stringify(options)})`)

    if (this._hasLeftActionPresence) return
    if (!this._channels.actionsChannel) {
      return
    }

    await userMessaging.leaveChannelPresence(
      this._channels.actionsChannel,
      this.id,
      options
    )
    this._hasLeftActionPresence = true
  }

  updateActionPresence = async (requiresGeoLocation=false) => {
    // Can be used in the future to periodically send the participants
    // status and/or geoLocation
    const args = [
      this._channels.actionsChannel,
      this.id,
      requiresGeoLocation
    ]
    if (this._urlContext) args.push(this._urlContext)
    await userMessaging.updateChannelPresence(...args)
  }

  leaveEvent = async (type) => {
    if (this._hasLeftEvent) return
    if (this._leaveEventPromise) return this._leaveEventPromise

    logger.debug(`AnearParticipant.leaveEvent(type: ${type})`)

    this._leaveEventPromise = (async () => {
      await this.leaveActionPresence({ type })
      await this.closeMessaging()
      this._hasLeftEvent = true
      logger.debug(`AnearParticipant.leaveEvent complete`)
    })()

    return this._leaveEventPromise
  }

  closeMessaging = async () => {
    return this.detachAll()
  }

  detachAll = async () => {
    if (this._detachingPromise) return this._detachingPromise
    if (!this._channels.actionsChannel && !this._channels.privateChannel && !this._channels.participantsChannel) return

    this._detachingPromise = (async () => {
      // Remove Ably message listeners before detach. Detach alone does not clear
      // subscriptions; the same channel name is reused on re-entry and listeners stack.
      this.unSubscribeEventPrivateMessages()
      this.unSubscribeEventParticipantsMessages()
      this.unSubscribeForceShutdownMessages()
      await Promise.all([
        userMessaging.detachChannel(this._channels.actionsChannel),
        userMessaging.detachChannel(this._channels.privateChannel),
        userMessaging.detachChannel(this._channels.participantsChannel)
      ])
      this._channels.actionsChannel = null
      this._channels.participantsChannel = null
      this._channels.privateChannel = null
    })()

    return this._detachingPromise
  }

  subscribeEventParticipantsMessages = callback => {
    // subscribe to the message channel that sends display content to ALL participants
    // simultaneously
    userMessaging.subscribeEventMessages(
      this._channels.participantsChannel, ParticipantsDisplayMessageType, callback, false
    )
  }

  subscribeEventPrivateMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.privateChannel, PrivateDisplayMessageType, callback, false)
  }

  unSubscribeEventParticipantsMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.participantsChannel, ParticipantsDisplayMessageType)
  }

  unSubscribeEventPrivateMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.privateChannel, PrivateDisplayMessageType)
  }

  subscribeForceShutdownMessages = callback => {
    userMessaging.subscribeEventMessages(this._channels.privateChannel, ForceShutdownMessageType, callback, false)
  }

  unSubscribeForceShutdownMessages = () => {
    userMessaging.unSubscribeEventMessages(this._channels.privateChannel, ForceShutdownMessageType)
  }

  publishAction = (actionJson) => {
    const message = {
      participantId: this.id,
      payload: actionJson
    }
    userMessaging.publishAction(this._channels.actionsChannel, message)
  }
}
